import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { captureError } from "@/lib/monitoring";
import type {
  ReferenceRangeCriticality,
  ReferenceRanges,
} from "@/types/observations";
import { Observation as ObservationResource } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import type { Observation } from "fhir/r4";
import {
  type ComponentDisplayData,
  VITALS_CODE_MAPPING,
  type ValueUnitDisplayData,
  type VitalDataMap,
  VitalsEnum,
  type VitalsObservationParams,
  type VitalsType,
} from "../config/vitalsTypes";

const CORE_VITALS: VitalsType[] = [VitalsEnum.HEIGHT, VitalsEnum.WEIGHT];

type VitalsObservationResponse = {
  observation: Observation;
  criticality?: ReferenceRangeCriticality | null;
  referenceRanges?: ReferenceRanges | null;
};

type VitalsObservationData<T extends VitalsType> = VitalDataMap[T] & {
  criticality?: ReferenceRangeCriticality | null;
  referenceRanges?: ReferenceRanges | null;
};

export const parseVitalsObservation = (
  obs: ObservationResource,
): VitalDataMap[VitalsType] | undefined => {
  // Check if this is an empty observation (no actual data, just resourceType)
  if (!obs.valueQuantity && !obs.component) {
    if (obs.id) {
      return {
        value: obs.valueString ?? null,
        unit: "",
        effectiveDateTime: obs.effectiveDateTime ?? "",
        values: null,
      } as ValueUnitDisplayData;
    }
    return {
      value: null,
      unit: "",
      effectiveDateTime: "",
      values: null,
    };
  }

  const effectiveDateTime = obs.effectiveDateTime ?? "";
  // Case 1: "value-unit" pattern (e.g., height, weight)
  if (obs.valueQuantity?.value != null && obs.valueQuantity?.unit != null) {
    return {
      value: obs.valueQuantity.value,
      unit: obs.valueQuantity.unit,
      effectiveDateTime,
    } as ValueUnitDisplayData;
  }
  // Case 2: "component" pattern (e.g., blood pressure)
  if (obs.component) {
    if (
      obs.component.length >= 2 &&
      obs.component[0].valueQuantity?.value != null &&
      obs.component[1].valueQuantity?.value != null
    ) {
      return {
        values: [
          obs.component[0].valueQuantity.value,
          obs.component[1].valueQuantity.value,
        ],
        effectiveDateTime,
      } as ComponentDisplayData;
    }
    return {
      values: null,
    } as ComponentDisplayData;
  }
  throw new Error(
    `Unknown vitals observation format: ${JSON.stringify(obs.toJson())}`,
  );
};

export const useVitalsObservation = <T extends VitalsType>(
  vitalsType: T,
  patientId?: string,
) => {
  return useApi<
    VitalsObservationResponse,
    VitalsObservationData<T> | undefined
  >({
    queryOptions: {
      enabled: Boolean(patientId),
      queryKey: QueryKeys.Patient.MedicalRecords.Vitals.DEFAULT(
        patientId,
        vitalsType,
      ),
      queryFn: async () => {
        const response = await axiosInstance.get<VitalsObservationResponse>(
          `/charting/vitals/${vitalsType}`,
          {
            params: { patientId },
          },
        );
        return response?.data;
      },

      select: (data): VitalsObservationData<T> | undefined => {
        try {
          const { observation, criticality, referenceRanges } = data;

          const obs = new ObservationResource(observation);
          const parsedData = parseVitalsObservation(obs) as VitalDataMap[T];

          return {
            ...parsedData,
            criticality,
            referenceRanges,
          } as VitalsObservationData<T>;
        } catch (error) {
          captureError(error as Error);
          return undefined;
        }
      },
    },
  });
};

export const useCreateVitalsObservation = (
  vitalsType: VitalsType,
  patientId: string,
) => {
  const queryClient = useQueryClient();

  return useApiMutation<VitalsObservationResponse, VitalsObservationParams>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Vitals.CREATE(
        patientId,
        vitalsType,
      ),
      mutationFn: async (data) => {
        const response = await axiosInstance.post(
          `/charting/vitals/${vitalsType}`,
          data,
        );
        return response.data;
      },
      onSuccess: (data) => {
        const patientId = new ObservationResource(data.observation).subject
          ?.identifier?.value as string;

        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Vitals.DEFAULT(
            patientId,
            vitalsType,
          ),
        });
        // "Core Vitals" query is used to display BMI, which is calculated from height and weight,
        // so invalidate the query to recalculate BMI
        if (CORE_VITALS.includes(vitalsType)) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.MedicalRecords.Vitals.CORE(patientId),
          });
          // Invalidate BMI query
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.MedicalRecords.Vitals.BMI(patientId),
          });
          // Also invalidate BMI history since BMI changes when height or weight changes
          queryClient.invalidateQueries({
            queryKey:
              QueryKeys.Patient.MedicalRecords.Vitals.BMI_HISTORY(patientId),
          });
        }
      },
    },
  });
};

interface VitalsObservationHistoryResponse {
  observations: Array<{
    observationDate: string;
    observation: Observation;
    groupItemAnswerType: null; // Always null for vitals
    criticality?: ReferenceRangeCriticality | null;
    referenceRanges?: ReferenceRanges | null;
  }>;
}

export const useVitalsObservationHistory = <T extends VitalsType>(
  vitalsType: T,
  patientId?: string,
  enabled = true,
) => {
  const { code, system } = VITALS_CODE_MAPPING[vitalsType];

  return useApi<VitalsObservationHistoryResponse>({
    queryOptions: {
      enabled: Boolean(patientId) && enabled,
      queryKey: QueryKeys.Patient.MedicalRecords.Vitals.HISTORY(
        patientId,
        vitalsType,
      ),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/charting/observation-generic/history",
          {
            params: {
              patientId,
              code,
              system: system.includes("loinc") ? "loinc" : "snomed",
            },
          },
        );
        return response?.data;
      },
    },
  });
};

interface VoidVitalsObservationVariables {
  observationId: string;
}

export const useVoidVitalsObservation = ({
  patientId,
  vitalsType,
  onSuccess,
}: {
  patientId?: string;
  vitalsType?: VitalsType;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<void, VoidVitalsObservationVariables>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.Vitals.VOID(
        patientId,
        vitalsType,
      ),
      mutationFn: ({ observationId }) => {
        return axiosInstance.delete(`/charting/vitals/${observationId}`);
      },
      onSuccess: () => {
        // Invalidate vitals queries
        if (patientId && vitalsType) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.MedicalRecords.Vitals.DEFAULT(
              patientId,
              vitalsType,
            ),
          });

          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.MedicalRecords.Vitals.HISTORY(
              patientId,
              vitalsType,
            ),
          });
        }

        // Invalidate core vitals query for BMI calculation
        if (patientId && vitalsType && CORE_VITALS.includes(vitalsType)) {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.MedicalRecords.Vitals.CORE(patientId),
          });
          // Invalidate BMI query
          queryClient.invalidateQueries({
            queryKey: QueryKeys.Patient.MedicalRecords.Vitals.BMI(patientId),
          });
          // Also invalidate BMI history so the BMI history modal refreshes
          queryClient.invalidateQueries({
            queryKey:
              QueryKeys.Patient.MedicalRecords.Vitals.BMI_HISTORY(patientId),
          });
        }

        onSuccess?.();
      },
    },
  });
};
